import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { rankingsApi } from "../../api/rankings";
import { formatCabinNumber, normalizeDivisionName } from "../../utils/terminology";
import type { RankingsResult, RankingMetric, RankingScope, RankingSortBy } from "../../types";

const METRIC_LABEL: Record<RankingMetric, string> = {
  acs: "Most ACs",
  honors: "Most Honors",
  graduations: "Most Graduations",
};
const SCOPE_LABEL: Record<RankingScope, string> = {
  "all-time": "All-Time",
  "current-term": "Current Term",
  active: "Active Campers",
};

const PRINT_STYLE = `
@media print {
  @page { size: letter portrait; margin: 0.5in; }
  .no-print { display: none !important; }
  body { background: white !important; }
}
@media screen {
  body { background: #f5f5f4; }
  .print-page { background: white; max-width: 8.5in; margin: 0 auto; padding: 0.75in; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
}
`;

export default function RankingsPrint() {
  const [params] = useSearchParams();
  const [data, setData] = useState<RankingsResult | null>(null);
  const [loading, setLoading] = useState(true);

  const metric = (params.get("metric") ?? "acs") as RankingMetric;
  const scope = (params.get("scope") ?? "all-time") as RankingScope;
  const sortBy = (params.get("sortBy") ?? "count") as RankingSortBy;
  const termId = params.get("termId") ? Number(params.get("termId")) : undefined;
  const divisionId = params.get("divisionId") ? Number(params.get("divisionId")) : undefined;
  const team = params.get("team");
  const cabinId = params.get("cabinId") ? Number(params.get("cabinId")) : undefined;
  const asOfDate = params.get("asOfDate") ?? undefined;
  const limit = params.get("limit") ? Number(params.get("limit")) : undefined;
  const termLabel = params.get("termLabel") ?? "";

  useEffect(() => {
    setLoading(true);
    rankingsApi
      .list({
        metric,
        scope,
        sortBy,
        termId,
        divisionId,
        team: team === "army" || team === "navy" ? team : undefined,
        cabinId,
        asOfDate,
        limit,
      })
      .then(setData)
      .catch(() => toast.error("Failed to load rankings"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  if (loading || !data) {
    return (
      <div className="p-8 text-center text-stone-500">Loading rankings…</div>
    );
  }

  const filters: string[] = [];
  if (divisionId) filters.push("Division filter applied");
  if (team) filters.push(`Team: ${team}`);
  if (cabinId) filters.push("Cabin filter applied");
  if (asOfDate) filters.push(`As of ${asOfDate}`);
  if (sortBy === "per-year") filters.push("Sorted by per-year average");
  if (limit) filters.push(`Top ${limit}`);

  return (
    <>
      <style>{PRINT_STYLE}</style>
      <div className="no-print fixed top-3 right-3 z-10">
        <button
          onClick={() => window.print()}
          className="bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold px-4 py-2 rounded-md shadow"
        >
          Print
        </button>
      </div>
      <div className="print-page text-stone-900">
        <header className="mb-6 border-b border-stone-300 pb-4">
          <h1 className="text-2xl font-bold">
            {METRIC_LABEL[metric]} &mdash; {SCOPE_LABEL[scope]}
          </h1>
          {scope === "current-term" && termLabel && (
            <div className="text-sm text-stone-600 mt-1">{termLabel}</div>
          )}
          {filters.length > 0 && (
            <div className="text-xs text-stone-500 mt-2">{filters.join(" · ")}</div>
          )}
          <div className="text-xs text-stone-500 mt-1">
            {data.totalCampers} campers ranked &middot; Printed {new Date().toLocaleDateString()}
          </div>
        </header>

        {data.rows.length === 0 ? (
          <p className="text-center text-stone-400 py-12">No ranked campers match these filters.</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-stone-400 text-left">
                <th className="py-1.5 pr-3 text-right w-12">Rank</th>
                <th className="py-1.5 pr-3">Camper</th>
                <th className="py-1.5 pr-3">Division</th>
                <th className="py-1.5 pr-3">Team</th>
                <th className="py-1.5 pr-3">Cabin</th>
                <th className="py-1.5 pr-3 text-right">{METRIC_LABEL[metric].replace("Most ", "")}</th>
                <th className="py-1.5 pr-3 text-right">Yrs</th>
                <th className="py-1.5 pr-3 text-right">Per Yr</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.camper.id} className="border-b border-stone-200">
                  <td className="py-1 pr-3 text-right font-semibold tabular-nums">{row.rank}</td>
                  <td className="py-1 pr-3 font-medium">
                    {row.camper.lastName}, {row.camper.firstName}
                    {row.camper.formerCamper && (
                      <span className="ml-1 text-xs italic text-stone-500">(staff)</span>
                    )}
                  </td>
                  <td className="py-1 pr-3 text-stone-700">{row.divisionName ? normalizeDivisionName(row.divisionName) : "—"}</td>
                  <td className="py-1 pr-3 text-stone-700">{row.camper.team ?? "—"}</td>
                  <td className="py-1 pr-3 text-stone-700">{row.cabinName ? formatCabinNumber(row.cabinName) : "—"}</td>
                  <td className="py-1 pr-3 text-right tabular-nums font-semibold">{row.count}</td>
                  <td className="py-1 pr-3 text-right tabular-nums text-stone-600">{row.yearsAttended}</td>
                  <td className="py-1 pr-3 text-right tabular-nums text-stone-600">{row.perYear.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
